import { useTranslation } from "react-i18next";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Legend,
  Tooltip,
} from "recharts";
import type { TBrokerAnalytics, TClaimStatusAnalytics } from "@/types/TAnalytics";

type SuccessRatePieChartProps = {
  broker: TBrokerAnalytics;
  claimStatus: TClaimStatusAnalytics;
};

const COLORS = {
  success: "#22c55e",
  failed: "#ef4444",
  blacklisted: "#f59e0b",
};

function MiniPie({
  data,
  title,
  total,
  successRate,
}: {
  data: { name: string; value: number; color: string }[];
  title: string;
  total: number;
  successRate: number;
}) {
  const { t } = useTranslation();

  if (total === 0) {
    return (
      <div className="text-center">
        <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">{title}</p>
        <div className="h-48 flex items-center justify-center text-gray-400 text-sm">
          {t("analytics.noData", "Keine Daten")}
        </div>
      </div>
    );
  }

  return (
    <div className="text-center">
      <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">{title}</p>
      <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
        {total} {t("analytics.kpi.queries", "Abfragen")} &middot; {successRate.toFixed(1)}% {t("analytics.successPie.success", "Erfolg")}
      </p>
      <div className="h-36">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              innerRadius={30}
              outerRadius={50}
              paddingAngle={2}
              dataKey="value"
            >
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
            <Tooltip formatter={(value) => [`${value} (${((Number(value) / total) * 100).toFixed(1)}%)`, ""]} />
            <Legend wrapperStyle={{ fontSize: "11px" }} />
          </PieChart>
        </ResponsiveContainer>
      </div>
      {/* Summary underneath */}
      <div className="flex justify-center gap-3 mt-1 text-xs">
        {data.map((d) => (
          <span key={d.name} className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full inline-block" style={{ backgroundColor: d.color }} />
            {d.value}
          </span>
        ))}
      </div>
    </div>
  );
}

export function SuccessRatePieChart({ broker, claimStatus }: SuccessRatePieChartProps) {
  const { t } = useTranslation();

  const brokerData = [
    { name: t("analytics.successPie.success", "Erfolg"), value: broker.success, color: COLORS.success },
    { name: t("analytics.successPie.failed", "Fehler"), value: broker.failed, color: COLORS.failed },
    { name: t("analytics.successPie.blacklisted", "Sonderfälle"), value: broker.blacklisted, color: COLORS.blacklisted },
  ].filter(d => d.value > 0);

  const claimData = [
    { name: t("analytics.successPie.success", "Erfolg"), value: claimStatus.success, color: COLORS.success },
    { name: t("analytics.successPie.failed", "Fehler"), value: claimStatus.failed, color: COLORS.failed },
  ].filter(d => d.value > 0);

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm border border-gray-200 dark:border-gray-700">
      <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">
        {t("analytics.successPie.title", "Erfolgsrate")}
      </h3>
      <div className="grid grid-cols-2 gap-4">
        <MiniPie
          data={brokerData}
          title={t("analytics.successPie.broker", "Berater-Abfragen")}
          total={broker.total}
          successRate={broker.success_rate}
        />
        <MiniPie
          data={claimData}
          title={t("analytics.successPie.claim", "Schadenstatus")}
          total={claimStatus.total}
          successRate={claimStatus.success_rate}
        />
      </div>
    </div>
  );
}
